// src/components/licenses/UploadLicensesCSV.js
import React, { useRef, useState } from 'react';
import {
  Button, LinearProgress, Box, Typography, Stack,
  FormControlLabel, Checkbox
} from '@mui/material';
import Papa from 'papaparse';
import { API_ENDPOINTS } from '../../../constants/apiEndpoints';
import { useAuth } from '../../../context/AuthContext';

const BATCH_SIZE = 500;

const csvTemplate = [
  [
    'date_received','date_due','site','site_poc','site_poc_email',
    'category','department','software','vendor','vendor_poc',
    'vendor_quote','vendor_poc_email','total','gl_account','status',
    'renewed_date','notes', 'description'
  ],
  // Example row (optional):
  // ['2025-01-01','2025-12-31','San Diego','Jane Doe','jane@site.com','Security','IT','Okta','Okta Inc.','John Rep','Q-123','john@okta.com','12000','654321','Purchased','2025-07-01','Renew yearly']
];

const downloadTemplate = () => {
  const csv = csvTemplate.map(r => r.map(v => `"${String(v ?? '').replace(/"/g,'""')}"`).join(',')).join('\n');
  const blob = new Blob([csv], { type: 'text/csv' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = 'licenses_template.csv';
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
};

const normalize = (row) => {
  const out = {};
  Object.keys(row).forEach((k) => {
    const key = (k || '').trim().toLowerCase();
    const v = row[k];
    if (v === undefined || v === null) out[key] = null;
    else if (typeof v === 'string') {
      const s = v.trim();
      out[key] = s === '' ? null : s;
    } else out[key] = v;
  });
  return out;
};

const UploadLicensesCSV = ({ onDone }) => {
  const { apiFetch } = useAuth();
  const inputRef = useRef(null);
  const [progress, setProgress] = useState(0);
  const [busy, setBusy] = useState(false);
  const [summary, setSummary] = useState(null);
  const [error, setError] = useState(null);
  const [replaceAll, setReplaceAll] = useState(false);
  const [phase, setPhase] = useState('Uploading');

  const postBatch = async (rows, mode = 'upsert') => {
    const resp = await apiFetch(API_ENDPOINTS.LICENSES_BULK_UPLOAD, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ mode, rows }),
    });
    if (!resp.ok) throw new Error(`HTTP ${resp.status}: ${await resp.text()}`);
    return resp.json();
  };

  // The backend has no server-side "replace" mode, so a full wipe is done here:
  // fetch every existing record and DELETE it by id before importing the file.
  const deleteAllExisting = async () => {
    const res = await apiFetch(API_ENDPOINTS.SOFTWARE_LICENSES);
    if (!res.ok) throw new Error(`Failed to load existing records: HTTP ${res.status}`);
    const data = await res.json();
    const ids = (Array.isArray(data) ? data : []).map(r => r.id).filter(Boolean);

    const CONCURRENCY = 8;
    let done = 0;
    for (let i = 0; i < ids.length; i += CONCURRENCY) {
      const slice = ids.slice(i, i + CONCURRENCY);
      await Promise.all(slice.map(async (id) => {
        const r = await apiFetch(API_ENDPOINTS.SOFTWARE_LICENSE_DETAIL(id), { method: 'DELETE' });
        if (!r.ok && r.status !== 404) throw new Error(`Delete failed for id ${id}: HTTP ${r.status}`);
      }));
      done += slice.length;
      setProgress(Math.round((done / ids.length) * 100));
    }
    return ids.length;
  };

  const handleFile = (file) => {
    if (!file) return;
    setError(null);
    setSummary(null);
    setProgress(0);
    setBusy(true);

    Papa.parse(file, {
      header: true,
      skipEmptyLines: true,
      transformHeader: (h) => (h || '').trim().toLowerCase(),
      complete: async (res) => {
        try {
          const rows = res.data.map(normalize);
          if (!rows.length) throw new Error('CSV has no rows.');

          // Optional quick header sanity check
          const must = ['software','vendor','site'];
          const headers = Object.keys(rows[0]);
          const missing = must.filter(m => !headers.includes(m));
          if (missing.length) throw new Error(`Missing required headers: ${missing.join(', ')}`);

          // Replace = delete every existing record first, then import. Confirm once.
          if (replaceAll && !window.confirm(
            `REPLACE ALL: this deletes every existing app record and replaces them ` +
            `with the ${rows.length} row(s) in this file. This cannot be undone. Continue?`
          )) {
            setBusy(false);
            if (inputRef.current) inputRef.current.value = '';
            return;
          }

          let deleted = 0;
          if (replaceAll) {
            setPhase('Deleting existing records');
            setProgress(0);
            deleted = await deleteAllExisting();
          }

          setPhase('Uploading');
          setProgress(0);
          let created = 0, updated = 0, errors = 0;
          for (let i = 0; i < rows.length; i += BATCH_SIZE) {
            const chunk = rows.slice(i, i + BATCH_SIZE);
            const resJson = await postBatch(chunk, 'upsert');
            created += resJson.created || 0;
            updated += resJson.updated || 0;
            errors  += resJson.errors  || 0;
            setProgress(Math.round(((i + chunk.length) / rows.length) * 100));
          }
          setSummary({ total: rows.length, created, updated, errors, deleted, replaced: replaceAll });
          if (onDone) onDone({ created, updated, errors });
        } catch (e) {
          setError(e.message || String(e));
        } finally {
          setBusy(false);
          setProgress(100);
          if (inputRef.current) inputRef.current.value = ''; // reset file input
        }
      },
      error: (err) => {
        setBusy(false);
        setError(err?.message || 'Failed to parse CSV.');
      }
    });
  };

  return (
    <Box>
      <Stack direction="row" spacing={1} alignItems="center">
        <Button variant="outlined" onClick={downloadTemplate}>Download CSV Template</Button>
        <input
          ref={inputRef}
          type="file"
          accept=".csv,text/csv"
          style={{ display: 'none' }}
          onChange={(e) => handleFile(e.target.files?.[0])}
        />
        <Button variant="contained" onClick={() => inputRef.current?.click()} disabled={busy}>
          Upload CSV (bulk)
        </Button>
        <FormControlLabel
          control={
            <Checkbox
              checked={replaceAll}
              onChange={(e) => setReplaceAll(e.target.checked)}
              disabled={busy}
              color="error"
            />
          }
          label="Replace all (wipe existing first)"
        />
      </Stack>

      {replaceAll && (
        <Typography variant="caption" color="error" sx={{ display: 'block', mt: 0.5 }}>
          Warning: uploading will delete every existing app record before importing this file.
        </Typography>
      )}

      {busy && (
        <Box mt={1}>
          <LinearProgress variant="determinate" value={progress} />
          <Typography variant="caption">{phase}… {progress}%</Typography>
        </Box>
      )}

      {summary && (
        <Typography variant="body2" mt={1}>
          Done{summary.replaced ? ` (deleted ${summary.deleted} existing)` : ''}. Total: {summary.total} • Created: {summary.created} • Updated: {summary.updated} • Errors: {summary.errors}
        </Typography>
      )}

      {error && (
        <Typography variant="body2" color="error" mt={1}>{error}</Typography>
      )}
    </Box>
  );
};

export default UploadLicensesCSV;